import { createFileRoute, Link } from "../lib/router-compat";
import { useState } from "react";
import { z } from "zod";
import { PanBackdrop } from "../components/PageShell";

const WEB3FORMS_ACCESS_KEY = "e633b570-ca8e-4533-b2bd-b5d7afe14309";
const WEB3FORMS_ENDPOINT = "https://api.web3forms.com/submit";

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: "Contact — Nigel P. Chase · The Pan Institute" },
      {
        name: "description",
        content:
          "Reach Nigel Chase, Director — Atlantic Clarion, Pan Institute, Maine Pan Productions. Bookings, lessons, custom-tuned pans.",
      },
      { property: "og:title", content: "Contact — The Pan Institute" },
      {
        property: "og:description",
        content:
          "Bookings, lessons, and custom-tuned pans. Brooksville, Maine.",
      },
    ],
  }),
  component: ContactPage,
});

const contactSchema = z.object({
  name: z.string().trim().min(1, "Required").max(100, "Too long"),
  email: z.string().trim().email("Invalid email").max(255, "Too long"),
  subject: z.string().trim().min(1, "Pick a subject").max(80, "Too long"),
  message: z.string().trim().min(1, "Required").max(2000, "Too long"),
});

const SUBJECTS = [
  "Booking — event or performance",
  "Classes & lessons",
  "Pan tuning or purchase",
  "Press / media",
  "Just saying hello",
] as const;

function ContactPage() {
  const [sent, setSent] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof z.infer<typeof contactSchema>, string>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const parsed = contactSchema.safeParse({
      name: fd.get("name"),
      email: fd.get("email"),
      subject: fd.get("subject"),
      message: fd.get("message"),
    });
    if (!parsed.success) {
      const fieldErrors: typeof errors = {};
      for (const issue of parsed.error.issues) {
        const k = issue.path[0] as keyof z.infer<typeof contactSchema>;
        if (!fieldErrors[k]) fieldErrors[k] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }
    setErrors({});
    setSubmitError(null);
    setSubmitting(true);

    try {
      const response = await fetch(WEB3FORMS_ENDPOINT, {
        method: "POST",
        headers: {
          Accept: "application/json",
        },
        body: new FormData(form),
      });

      const data = (await response.json().catch(() => null)) as
        | { success?: boolean; message?: string }
        | null;

      if (!response.ok || !data?.success) {
        throw new Error(data?.message || "Something went wrong. Please try again.");
      }

      form.reset();
      setSent(true);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen text-foreground antialiased">
      <PanBackdrop />

      {/* Hero */}
      <section className="relative w-full border-b border-border/40 overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_70%_20%,color-mix(in_oklab,var(--copper)_22%,transparent),transparent_60%)]"
        />
        <div
          aria-hidden
          className="absolute inset-x-0 top-0 -z-10 h-px bg-gradient-to-r from-transparent via-[var(--copper)]/40 to-transparent"
        />
        <div className="mx-auto max-w-5xl px-5 pt-36 pb-20 md:pt-44 md:pb-28">
          <div className="grid md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-8">
              <h1 className="font-display text-4xl md:text-7xl font-light tracking-[-0.02em] leading-[0.95]">
                Let's make
                <br />
                <span className="italic text-[var(--copper-glow)]">something happen.</span>
              </h1>
              <p className="mt-8 max-w-xl text-base md:text-lg text-foreground/70 leading-relaxed font-light">
                Bookings, classes, custom-tuned pans, or a question about the
                program — write directly to Nigel and we'll be in touch within
                a few days.
              </p>
            </div>
            <div className="md:col-span-4 md:text-right text-[11px] uppercase tracking-[0.3em] text-foreground/55">
              <div>Brooksville, Maine</div>
              <div className="mt-1 text-foreground/35">Est. 1994</div>
            </div>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="mx-auto max-w-5xl px-5 py-20 md:py-28">
        <div className="grid md:grid-cols-12 gap-12">
          {/* Sidebar — direct contact */}
          <aside className="md:col-span-4 space-y-10">
            <ContactBlock
              label="Email"
              primary={
                <a
                  href="mailto:nigelchase@gmail.com"
                  className="text-foreground hover:text-[var(--copper-glow)] transition-colors break-all"
                >
                  nigelchase@gmail.com
                </a>
              }
              secondary="Best for bookings & lesson inquiries"
            />
            <ContactBlock
              label="Studio"
              primary={<span>The New Pan Facility</span>}
              secondary="150 Red Trail · Brooksville, Maine"
            />
            <ContactBlock
              label="Director"
              primary={<span>Nigel P. Chase</span>}
              secondary={
                <Link
                  to="/nigel"
                  className="hover:text-[var(--copper-glow)] transition-colors underline-offset-4 hover:underline"
                >
                  Read his story →
                </Link>
              }
            />

            <div className="pt-8 border-t border-border/40">
              <div className="text-[10px] uppercase tracking-[0.4em] text-[var(--copper)] mb-3">
                Response time
              </div>
              <p className="text-sm text-foreground/65 leading-relaxed font-light">
                Usually within 2–3 days. For time-sensitive bookings, mention
                the date in your subject line.
              </p>
            </div>
          </aside>

          {/* Form */}
          <div className="md:col-span-8">
            {sent ? (
              <SentCard onReset={() => setSent(false)} />
            ) : (
              <form
                onSubmit={onSubmit}
                noValidate
                className="rounded-2xl border border-border/40 bg-card/30 backdrop-blur-sm p-6 md:p-10 space-y-7"
              >
                <input type="hidden" name="access_key" value={WEB3FORMS_ACCESS_KEY} />
                <input type="hidden" name="subject" value="Contact form submission from The Pan Institute" />
                <input type="hidden" name="from_name" value="The Pan Institute Website" />
                <input type="checkbox" name="botcheck" tabIndex={-1} aria-hidden="true" className="hidden" />

                <header className="pb-2">
                  <div className="text-[10px] uppercase tracking-[0.4em] text-[var(--copper)] mb-3">
                    Send a message
                  </div>
                  <h2 className="font-display text-2xl md:text-3xl font-light tracking-[-0.01em]">
                    Tell us a little about what you have in mind.
                  </h2>
                </header>

                <div className="grid sm:grid-cols-2 gap-6">
                  <Field label="Name" error={errors.name}>
                    <input
                      name="name"
                      type="text"
                      maxLength={100}
                      autoComplete="name"
                      className="form-input"
                    />
                  </Field>
                  <Field label="Email" error={errors.email}>
                    <input
                      name="email"
                      type="email"
                      maxLength={255}
                      autoComplete="email"
                      className="form-input"
                    />
                  </Field>
                </div>

                <Field label="Subject" error={errors.subject}>
                  <select name="subject" defaultValue="" className="form-input appearance-none cursor-pointer">
                    <option value="" disabled className="bg-background">
                      Choose one…
                    </option>
                    {SUBJECTS.map((s) => (
                      <option key={s} value={s} className="bg-background">
                        {s}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Message" error={errors.message}>
                  <textarea
                    name="message"
                    rows={6}
                    maxLength={2000}
                    className="form-input resize-none"
                    placeholder="Date, venue, group size, anything we should know…"
                  />
                </Field>

                <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
                  <p className="text-[11px] text-foreground/45 leading-relaxed">
                    We'll never share your email. By sending you agree to be
                    contacted in reply.
                  </p>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="group inline-flex items-center justify-center gap-3 px-7 py-3.5 bg-[var(--copper)] text-black text-[11px] tracking-[0.3em] uppercase rounded-full hover:bg-[var(--copper-glow)] transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {submitting ? "Sending…" : "Send Message"}
                    <span aria-hidden className="transition-transform group-hover:translate-x-0.5">→</span>
                  </button>
                </div>
                {submitError && (
                  <p className="text-sm text-red-300" role="alert">
                    {submitError}
                  </p>
                )}
              </form>
            )}
          </div>
        </div>
      </section>

      <style>{`
        .form-input {
          width: 100%;
          background: transparent;
          border: 0;
          border-bottom: 1px solid color-mix(in oklab, var(--foreground) 20%, transparent);
          outline: none;
          padding: 0.6rem 0;
          font-size: 0.95rem;
          color: var(--foreground);
          transition: border-color 200ms ease;
        }
        .form-input:focus {
          border-bottom-color: var(--copper);
        }
        .form-input::placeholder {
          color: color-mix(in oklab, var(--foreground) 35%, transparent);
        }
      `}</style>

    </div>
  );
}

function ContactBlock({
  label,
  primary,
  secondary,
}: {
  label: string;
  primary: React.ReactNode;
  secondary?: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.4em] text-[var(--copper)] mb-3">
        {label}
      </div>
      <div className="font-display italic text-2xl md:text-3xl font-light leading-tight">
        {primary}
      </div>
      {secondary && (
        <div className="mt-2 text-[12px] text-foreground/55 leading-relaxed">
          {secondary}
        </div>
      )}
    </div>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.3em] text-foreground/60">
        {label}
      </span>
      {children}
      {error && (
        <span className="mt-1 block text-[11px] text-destructive">{error}</span>
      )}
    </label>
  );
}

function SentCard({ onReset }: { onReset: () => void }) {
  return (
    <div className="rounded-2xl border border-[var(--copper)]/40 bg-card/30 p-10 md:p-14 text-center">
      <div className="mx-auto h-12 w-12 rounded-full border border-[var(--copper)]/60 flex items-center justify-center text-[var(--copper-glow)] text-xl">
        ✓
      </div>
      <h2 className="mt-6 font-display text-3xl md:text-4xl font-light tracking-[-0.01em]">
        Message sent.
      </h2>
      <p className="mt-3 text-foreground/65 max-w-md mx-auto leading-relaxed">
        Thanks for reaching out — we'll get back to you within a few days.
      </p>
      <button
        onClick={onReset}
        className="mt-8 text-[11px] uppercase tracking-[0.3em] text-[var(--copper-glow)] hover:underline underline-offset-4"
      >
        Send another →
      </button>
    </div>
  );
}
